import { useState, useEffect, ReactNode } from "react";
import { X } from "lucide-react";
import { Dialog, DialogTrigger, DialogContent, DialogTitle } from "@/components/ui/dialog";
import SmsConsentField from "@/components/SmsConsentField";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { REVIEW_STATS, REVIEW_RATING_TEXT } from "@/data/reviewStats";

const serviceOptions = [
  "Product Consultation & Specifying",
  "Hardwood Installation",
  "Sanding & Dustless Refinishing",
  "Subfloor Leveling & Repair",
  "Subfloor Evaluation & Correction",
  "Reclaimed Wood & Beams",
  "NWFA Inspection Services",
];

interface QuoteModalProps {
  trigger: ReactNode;
}

const QuoteModal = ({ trigger }: QuoteModalProps) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("");
  const [location, setLocation] = useState("");
  const [smsConsent, setSmsConsent] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (submitted && typeof (window as any).gtag === "function") {
      (window as any).gtag("event", "conversion", {
        send_to: "AW-1018637632/h8CpCLuApbMcEMDa3OUD",
        value: 200.0,
        currency: "USD",
      });
    }
  }, [submitted]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!smsConsent) return;
    setSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("pipedrive-lead", {
        body: { name, email, phone, service, location, smsConsent },
      });

      if (error) throw error;
      setSubmitted(true);
    } catch (err) {
      console.error("Pipedrive submission error:", err);
      toast({
        title: "We received your request",
        description: "Our team has been notified and will contact you within 24 hours.",
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (val: boolean) => {
    setOpen(val);
    if (!val) {
      setTimeout(() => {
        setSubmitted(false);
        setName("");
        setEmail("");
        setPhone("");
        setService("");
        setLocation("");
        setSmsConsent("");
      }, 300);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-white/5 backdrop-blur-md border border-white/10 shadow-2xl rounded-2xl p-0 max-w-md w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto gap-0 [&>button]:hidden">
        <DialogTitle className="sr-only">Schedule a Consultation</DialogTitle>

        {/* Close button */}
        <button
          onClick={() => handleOpenChange(false)}
          className="absolute top-3 right-3 text-primary-foreground/60 hover:text-primary-foreground transition-colors z-10 p-2 rounded-full bg-white/10 backdrop-blur-sm"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {submitted ? (
          <div className="p-8 text-center">
            <div className="text-3xl mb-3">✓</div>
            <h3 className="font-display text-xl font-semibold text-primary-foreground mb-2">Thank You!</h3>
            <p className="text-sm text-primary-foreground/50">We'll be in touch within 24 hours.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 md:p-8">
            <h3 className="font-display text-xl md:text-2xl font-semibold text-primary-foreground leading-tight">
              Schedule a Showroom Consultation.
            </h3>
            <p className="text-xs text-primary-foreground/45 mt-1.5 mb-6 leading-relaxed">
              Visit our Livonia Design Center to view our extensive collections and consult with our NWFA Certified team. Please provide your information below, and we will contact you to secure your appointment time.
            </p>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="First & Last Name"
                className="col-span-1 bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-white/50 focus:outline-none focus:bg-white/10 focus:border-primary focus:ring-1 focus:ring-primary transition-all"
              />
              <input
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone Number"
                className="col-span-1 bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-white/50 focus:outline-none focus:bg-white/10 focus:border-primary focus:ring-1 focus:ring-primary transition-all"
              />
            </div>

            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email Address"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-white/50 focus:outline-none focus:bg-white/10 focus:border-primary focus:ring-1 focus:ring-primary transition-all mb-3"
            />

            <div className="relative mb-3">
              <select
                required
                value={service}
                onChange={(e) => setService(e.target.value)}
                className={`w-full appearance-none bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:bg-white/10 focus:ring-1 focus:ring-primary focus:border-primary transition-all pr-9 ${
                  service ? "text-primary-foreground" : "text-primary-foreground/50"
                }`}
              >
                <option value="" disabled>What do you need help with?</option>
                {serviceOptions.map((opt) => (
                  <option key={opt} value={opt} className="bg-[hsl(var(--foreground))] text-primary-foreground">
                    {opt}
                  </option>
                ))}
              </select>
              <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-foreground/40 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            <input
              type="text"
              required
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., Northville, MI"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-white/50 focus:outline-none focus:bg-white/10 focus:border-primary focus:ring-1 focus:ring-primary transition-all mb-4"
            />

            <div className="mb-5">
              <SmsConsentField value={smsConsent} onChange={setSmsConsent} variant="light" />
            </div>

            <button
              type="submit"
              disabled={!smsConsent || submitting}
              className="w-full bg-primary text-primary-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-gold-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Submitting..." : "Request Showroom Appointment"}
            </button>

            <p className="text-[10px] text-primary-foreground/35 text-center mt-3.5 leading-relaxed">
              {REVIEW_RATING_TEXT} ★ · {REVIEW_STATS.count} Google Reviews&nbsp;&nbsp;&nbsp;🔒 Your privacy is protected.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteModal;
